'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { Clock3, PhoneCall, Presentation, ShieldCheck, Database, Rocket } from 'lucide-react';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { useWebsitePagesCopy } from '@/lib/cadrino-landing/useWebsitePagesCopy';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { Container, Section, PrimaryButton, SecondaryButton, MotionBlock } from './shared';
import CadrinoContactForm from './CadrinoContactForm';
type ContactSectionProps = {
  title?: string;
  subtitle?: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  showPageLinks?: boolean;
  qualifierChips?: string[];
};
export function ContactSection(t0) {
  const $ = _c(64);
  const {
    title,
    subtitle,
    primaryLabel,
    primaryHref: t1,
    secondaryLabel,
    secondaryHref: t2,
    showPageLinks: t3,
    qualifierChips
  } = t0;
  const primaryHref = t1 === undefined ? "#contact-form" : t1;
  const secondaryHref = t2 === undefined ? "/product" : t2;
  const showPageLinks = t3 === undefined ? false : t3;
  const c = useCadrinoCopy();
  const pages = useWebsitePagesCopy();
  const {
    language
  } = useLandingLanguage();
  const isEn = language === "en";
  let t4;
  if ($[0] !== pages.nav.product) {
    t4 = {
      href: "/product",
      label: pages.nav.product
    };
    $[0] = pages.nav.product;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== pages.nav.partners) {
    t5 = {
      href: "/partners",
      label: pages.nav.partners
    };
    $[2] = pages.nav.partners;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== pages.nav.security) {
    t6 = {
      href: "/security",
      label: pages.nav.security
    };
    $[4] = pages.nav.security;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== pages.nav.integrations) {
    t7 = {
      href: "/integrations",
      label: pages.nav.integrations
    };
    $[6] = pages.nav.integrations;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = [t4, t5, t6, t7];
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const pageLinks = t8;
  const resolvedTitle = title ?? c.cta.title;
  const resolvedSubtitle = subtitle ?? c.cta.subtitle;
  const resolvedPrimary = primaryLabel ?? c.cta.primary;
  const resolvedSecondary = secondaryLabel ?? pages.nav.product;
  const flowTitle = isEn ? "What happens after you submit" : "\u0160ta se de\u0161ava posle slanja";
  let t9;
  if ($[13] !== isEn) {
    t9 = isEn ? ["Reply within one business day.", "15-minute discovery call.", "Live demo for your exact workflow."] : ["Odgovor u roku od jednog radnog dana.", "Kratak discovery poziv (15 min).", "Live demo za va\u0161 konkretan workflow."];
    $[13] = isEn;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const flowSteps = t9;
  const trustTitle = isEn ? "Why teams send this form" : "Za\u0161to timovi \u0161alju ovaj upit";
  let t10;
  if ($[15] !== isEn) {
    t10 = isEn ? ["No-commitment demo.", "Data stays in your control.", "Rollout without disruption."] : ["Demo bez obaveze.", "Podaci ostaju pod va\u0161om kontrolom.", "Rollout bez prekida rada."];
    $[15] = isEn;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const trustItems = t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [Clock3, PhoneCall, Presentation];
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const stepIcons = t11 as const;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [ShieldCheck, Database, Rocket];
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const trustIcons = t12 as const;
  let t13;
  if ($[19] !== resolvedTitle) {
    t13 = <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-slate-50">{resolvedTitle}</h2>;
    $[19] = resolvedTitle;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== resolvedSubtitle) {
    t14 = <p className="mt-4 text-lg text-cadrino-muted">{resolvedSubtitle}</p>;
    $[21] = resolvedSubtitle;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== qualifierChips) {
    t15 = qualifierChips && qualifierChips.length > 0 && <div className="mt-4 flex flex-wrap gap-2.5">{qualifierChips.map(_temp)}</div>;
    $[23] = qualifierChips;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== primaryHref || $[26] !== resolvedPrimary) {
    t16 = <PrimaryButton href={primaryHref}>{resolvedPrimary}</PrimaryButton>;
    $[25] = primaryHref;
    $[26] = resolvedPrimary;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== resolvedSecondary || $[29] !== secondaryHref) {
    t17 = <SecondaryButton href={secondaryHref}>{resolvedSecondary}</SecondaryButton>;
    $[28] = resolvedSecondary;
    $[29] = secondaryHref;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <div className="mt-8 flex flex-wrap gap-3">{t16}{t17}</div>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== pageLinks || $[35] !== showPageLinks) {
    t19 = showPageLinks && <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm">{pageLinks.map(_temp2)}</div>;
    $[34] = pageLinks;
    $[35] = showPageLinks;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== flowTitle) {
    t20 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-accent">{flowTitle}</p>;
    $[37] = flowTitle;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== flowSteps) {
    t21 = <ol className="mt-3 space-y-2.5 text-sm text-cadrino-muted">{flowSteps.map((step, index) => <li key={step} className="flex items-start gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-white/[0.04]"><span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-md bg-cadrino-primary/15 text-cadrino-primary">{(() => {
            const Icon = stepIcons[index] ?? Clock3;
            return <Icon className="h-3.5 w-3.5" />;
          })()}</span><span>{step}</span></li>)}</ol>;
    $[39] = flowSteps;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <div className="rounded-2xl border border-white/[0.12] bg-white/[0.04] p-5">{t20}{t21}</div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== trustTitle) {
    t23 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-accent">{trustTitle}</p>;
    $[44] = trustTitle;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== trustItems) {
    t24 = <ul className="mt-3 space-y-2 text-sm text-cadrino-muted">{trustItems.map((item, index_0) => <li key={item} className="flex items-start gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-white/[0.04]"><span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-md bg-cadrino-primary/15 text-cadrino-primary">{(() => {
            const Icon_0 = trustIcons[index_0] ?? ShieldCheck;
            return <Icon_0 className="h-3.5 w-3.5" />;
          })()}</span><span>{item}</span></li>)}</ul>;
    $[46] = trustItems;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t23 || $[49] !== t24) {
    t25 = <div className="rounded-2xl border border-white/[0.12] bg-white/[0.03] p-5">{t23}{t24}</div>;
    $[48] = t23;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t22 || $[52] !== t25) {
    t26 = <div className="mt-8 space-y-4">{t22}{t25}</div>;
    $[51] = t22;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t18 || $[58] !== t19 || $[59] !== t26) {
    t27 = <MotionBlock>{t13}{t14}{t15}{t18}{t19}{t26}</MotionBlock>;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t18;
    $[58] = t19;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <MotionBlock delay={0.08}><div id="contact-form"><CadrinoContactForm /></div></MotionBlock>;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27) {
    t29 = <Section id="contact" className="border-t border-white/[0.08]"><Container><div className="grid gap-12 lg:grid-cols-2 lg:gap-16">{t27}{t28}</div></Container></Section>;
    $[62] = t27;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
}
function _temp2(link) {
  return <Link key={link.href} href={link.href} className="text-cadrino-muted transition-colors hover:text-slate-100">{link.label}</Link>;
}
function _temp(chip) {
  return <span key={chip} className="inline-flex items-center rounded-full border border-white/[0.1] bg-white/[0.04] px-3 py-1.5 text-xs text-slate-300">{chip}</span>;
}